<template>
  <v-container>
    <v-card class="elevation-8 rounded-lg">
      <!-- Header -->
      <v-card-title class="primary white--text">
        <v-icon left color="white" size="28">mdi-account-group</v-icon>
        <span class="text-h5">รายงานอัตรากำลังของวิทยาลัย แยกตามประเภทวิชา</span>
        <v-spacer></v-spacer>
        
        <!-- View Toggle -->
        <v-btn-toggle
          v-model="viewMode"
          mandatory
          class="mr-2"
        >
          <v-btn small value="chart" color="white" text>
            <v-icon>mdi-chart-bar</v-icon>
          </v-btn>
          <v-btn small value="table" color="white" text>
            <v-icon>mdi-table</v-icon>
          </v-btn>
        </v-btn-toggle>

        <!-- Refresh Button -->
        <v-btn
          icon
          color="white"
          @click="refreshData"
          :loading="loading"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Summary Statistics -->
      <v-card-text class="pb-2">
        <v-row>
          <v-col cols="12" md="3" v-for="(stat, index) in summaryStats" :key="index">
            <v-card
              :color="stat.color"
              class="white--text text-center stat-card"
              elevation="4"
            >
              <v-card-text class="py-3">
                <v-icon size="28" class="mb-1">{{ stat.icon }}</v-icon>
                <div class="text-h5 font-weight-bold">{{ stat.value }}</div>
                <div class="text-subtitle-2">{{ stat.title }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Chart View -->
      <v-card-text v-if="viewMode === 'chart'">
        <v-row>
          <!-- Chart Controls -->
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedChartType"
              :items="chartTypes"
              label="ประเภทกราฟ"
              outlined
              dense
              prepend-icon="mdi-chart-line"
              @change="updateChart"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="chartDataLimit"
              :items="dataLimitOptions"
              label="จำนวนข้อมูลที่แสดง"
              outlined
              dense
              prepend-icon="mdi-format-list-numbered"
              @change="updateChart"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="sortOrder"
              :items="sortOptions"
              label="เรียงลำดับ"
              outlined
              dense
              prepend-icon="mdi-sort"
              @change="updateChart"
            />
          </v-col>
        </v-row>

        <!-- Chart Container -->
        <v-card outlined class="chart-container mb-4">
          <v-card-title class="text-center pb-2">
            <span class="text-h6">{{ chartTitle }}</span>
          </v-card-title>
          <v-card-text>
            <div id="chart" style="height: 500px;">
              <apexchart
                :type="selectedChartType"
                height="500"
                :options="chartOptions"
                :series="series"
              />
            </div>
          </v-card-text>
        </v-card>

        <!-- Quick Stats Cards -->
        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined class="stats-detail-card">
              <v-card-title class="text-h6 success--text">
                <v-icon left color="success">mdi-trending-up</v-icon>
                สาขาวิชาที่มีบุคลากรมากที่สุด
              </v-card-title>
              <v-card-text>
                <div v-if="topBranch">
                  <div class="text-h6 font-weight-bold">{{ topBranch.branch_name_th }}</div>
                  <div class="text-subtitle-1 text--secondary">{{ topBranch.subject_typeName }}</div>
                  <v-chip color="success" text-color="white" class="mt-2">
                    {{ topBranch.countAll }} คน
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="stats-detail-card">
              <v-card-title class="text-h6 info--text">
                <v-icon left color="info">mdi-chart-pie</v-icon>
                การกระจายตัวของบุคลากร
              </v-card-title>
              <v-card-text>
                <div class="d-flex justify-space-between align-center mb-2">
                  <span>ค่าเฉลี่ย</span>
                  <v-chip small>{{ averagePersonnel }} คน</v-chip>
                </div>
                <div class="d-flex justify-space-between align-center mb-2">
                  <span>ค่ามัธยฐาน</span>
                  <v-chip small>{{ medianPersonnel }} คน</v-chip>
                </div>
                <div class="d-flex justify-space-between align-center">
                  <span>ส่วนเบียงเบนมาตรฐาน</span>
                  <v-chip small>{{ standardDeviation }}</v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Table View -->
      <v-card-text v-if="viewMode === 'table'" class="pa-0">
        <!-- Search and Filters -->
        <v-card-subtitle class="pb-0">
          <v-row align="center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="ค้นหาสาขาวิชาหรือประเภทวิชา"
                single-line
                hide-details
                dense
                outlined
                clearable
                class="search-field"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="subjectTypeFilter"
                :items="subjectTypes"
                label="ประเภทวิชา"
                outlined
                dense
                clearable
                hide-details
              />
            </v-col>
            <v-col cols="12" md="3" class="text-right">
              <!-- Export Menu -->
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="success"
                    v-bind="attrs"
                    v-on="on"
                    small
                  >
                    <v-icon left>mdi-download</v-icon>
                    ส่งออก
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="exportToExcel">
                    <v-list-item-icon>
                      <v-icon>mdi-file-excel</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Excel</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportToPDF">
                    <v-list-item-icon>
                      <v-icon>mdi-file-pdf-box</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>PDF</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChart">
                    <v-list-item-icon>
                      <v-icon>mdi-chart-bar</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>กราฟ (PNG)</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-subtitle>

        <!-- Enhanced Data Table -->
        <v-data-table
          :loading="loading"
          :headers="enhancedHeaders"
          :items="filteredPersonnelData"
          :search="search"
          :items-per-page="15"
          :footer-props="{
            'items-per-page-options': [10, 15, 25, 50, -1],
            'items-per-page-text': 'รายการต่อหน้า:'
          }"
          loading-text="กำลังโหลดข้อมูล..."
          no-data-text="ไม่มีข้อมูล"
          class="enhanced-table"
          fixed-header
          height="500"
          sort-by="countAll"
          sort-desc
        >
          <!-- Subject Type with Color -->
          <template v-slot:item.subject_typeName="{ item }">
            <v-chip
              :color="getSubjectTypeColor(item.subject_typeName)"
              text-color="white"
              small
              label
            >
              {{ item.subject_typeName }}
            </v-chip>
          </template>

          <!-- Branch Code -->
          <template v-slot:item.id_course_branch="{ item }">
            <span class="font-weight-bold text-primary">{{ item.id_course_branch }}</span>
          </template>

          <!-- Branch Name -->
          <template v-slot:item.branch_name_th="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.branch_name_th }}</div>
              <div class="text-caption text--secondary">รหัส: {{ item.id_course_branch }}</div>
            </div>
          </template>

          <!-- Personnel Count with Progress -->
          <template v-slot:item.countAll="{ item }">
            <div class="d-flex align-center">
              <v-chip
                :color="getCountColor(item.countAll)"
                text-color="white"
                class="mr-2"
              >
                {{ item.countAll }} คน
              </v-chip>
              <v-progress-linear
                :value="getProgressValue(item.countAll)"
                :color="getCountColor(item.countAll)"
                height="6"
                class="flex-grow-1"
                rounded
              />
            </div>
          </template>

          <!-- Percentage -->
          <template v-slot:item.percentage="{ item }">
            <v-chip
              small
              outlined
              :color="getCountColor(item.countAll)"
            >
              {{ ((item.countAll / totalPersonnel) * 100).toFixed(1) }}%
            </v-chip>
          </template>

          <!-- No Results -->
          <template v-slot:no-results>
            <v-alert
              :value="true"
              color="info"
              icon="mdi-information"
              class="ma-4"
            >
              ไม่พบผลลัพธ์สำหรับ "{{ search }}"
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      <v-icon left>{{ snackbar.icon }}</v-icon>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import VueMoment from "vue-moment";
import moment from "moment-timezone";

export default {
  name: "personnel_etc",
  
  data() {
    return {
      VueMoment,
      moment,
      loading: false,
      ApiKey: "HRvec2021",
      viewMode: "chart",
      
      // Data
      personnel_etcs: [],
      search: "",
      subjectTypeFilter: null,
      
      // Chart Configuration
      selectedChartType: "bar",
      chartDataLimit: 15,
      sortOrder: "desc",
      
      chartTypes: [
        { text: "กราฟแท่งแนวนอน", value: "bar" },
        { text: "กราฟแท่งแนวตั้ง", value: "column" },
        { text: "กราฟเส้น", value: "line" },
        { text: "กราฟพื้นที่", value: "area" },
        { text: "กราฟวงกลม", value: "pie" },
        { text: "กราฟโดนัท", value: "donut" }
      ],
      
      dataLimitOptions: [
        { text: "10 อันดับแรก", value: 10 },
        { text: "15 อันดับแรก", value: 15 },
        { text: "20 อันดับแรก", value: 20 },
        { text: "ทั้งหมด", value: -1 }
      ],
      
      sortOptions: [
        { text: "มากไปน้อย", value: "desc" },
        { text: "น้อยไปมาก", value: "asc" },
        { text: "ตามตัวอักษร", value: "alpha" }
      ],

      // Chart Data
      series: [],
      chartOptions: {},
      
      // Snackbar
      snackbar: {
        show: false,
        color: "success",
        timeout: 3000,
        icon: "mdi-check",
        text: ""
      },

      // Enhanced Headers
      enhancedHeaders: [
        {
          text: "ประเภทวิชา",
          align: "left",
          value: "subject_typeName",
          width: "150",
          sortable: true
        },
        {
          text: "รหัสสาขา",
          align: "center",
          value: "id_course_branch",
          width: "120",
          sortable: true
        },
        {
          text: "สาขาวิชา",
          align: "left",
          value: "branch_name_th",
          width: "300",
          sortable: true
        },
        {
          text: "จำนวนบุคลากร",
          align: "center",
          value: "countAll",
          width: "200",
          sortable: true
        },
        {
          text: "ร้อยละ",
          align: "center",
          value: "percentage",
          width: "100",
          sortable: false
        }
      ]
    };
  },

  async mounted() {
    await this.personnel_etcQueryAll();
  },

  methods: {
    async personnel_etcQueryAll() {
      this.loading = true;
      try {
        const result = await this.$http.post("personnel_etc.php", {
          ApiKey: this.ApiKey,
          group_branch: "Ok"
        });
        
        // Filter data
        const datafilter = result.data;
        this.personnel_etcs = datafilter.filter(value => 
          value.countAll != '0' && 
          String(value.personnel_etc_branch).length > 4 && 
          String(value.id_course_branch).length > 4
        );
        
        this.updateChart();
        this.showSnackbar("โหลดข้อมูลเรียบร้อยแล้ว", "success");
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    updateChart() {
      if (!this.personnel_etcs.length) return;
      
      let data = [...this.personnel_etcs];
      
      // Sort data
      if (this.sortOrder === "desc") {
        data.sort((a, b) => parseInt(b.countAll) - parseInt(a.countAll));
      } else if (this.sortOrder === "asc") {
        data.sort((a, b) => parseInt(a.countAll) - parseInt(b.countAll));
      } else if (this.sortOrder === "alpha") {
        data.sort((a, b) => a.branch_name_th.localeCompare(b.branch_name_th));
      }
      
      // Limit data
      if (this.chartDataLimit > 0) {
        data = data.slice(0, this.chartDataLimit);
      }
      
      const labelText = data.map(value => value.branch_name_th);
      const valuesst1 = data.map(value => parseInt(value.countAll));
      
      // Update series
      if (this.selectedChartType === "pie" || this.selectedChartType === "donut") {
        this.series = valuesst1;
      } else {
        this.series = [{
          name: 'จำนวนบุคลากร',
          data: valuesst1,
          color: '#455A64'
        }];
      }
      
      // Update chart options
      this.chartOptions = this.getChartOptions(labelText, data);
    },

    getChartOptions(labelText, data) {
      const baseOptions = {
        chart: {
          height: 500,
          type: this.selectedChartType === "column" ? "bar" : this.selectedChartType,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        colors: ['#B3151B', '#424242', '#E57373', '#9E9E9E', '#8E1014'],
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + " คน";
          },
          style: {
            fontSize: '11px',
            fontWeight: 'bold',
            colors: ["#B3151B"]
          }
        },
        title: {
          text: this.chartTitle,
          align: 'center',
          style: {
            fontSize: '16px',
            fontWeight: 'bold'
          }
        },
        legend: {
          show: this.selectedChartType === "pie" || this.selectedChartType === "donut",
          position: 'bottom'
        },
        responsive: [{
          breakpoint: 768,
          options: {
            chart: {
              height: 400
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      };

      if (this.selectedChartType === "pie" || this.selectedChartType === "donut") {
        return {
          ...baseOptions,
          labels: labelText,
          plotOptions: {
            pie: {
              donut: {
                size: this.selectedChartType === "donut" ? '60%' : '0%'
              }
            }
          }
        };
      }

      if (this.selectedChartType === "bar") {
        return {
          ...baseOptions,
          plotOptions: {
            bar: {
              horizontal: true,
              distributed: true,
              barHeight: '70%'
            }
          },
          xaxis: {
            categories: labelText,
            labels: {
              formatter: function (val) {
                return val + " คน";
              }
            }
          },
          yaxis: {
            labels: {
              show: true,
              maxWidth: 200,
              style: {
                fontSize: '10px'
              }
            }
          },
          grid: {
            borderColor: '#e0e0e0',
            strokeDashArray: 5
          }
        };
      }

      return {
        ...baseOptions,
        plotOptions: {
          bar: {
            horizontal: false,
            distributed: true,
            columnWidth: '70%'
          }
        },
        xaxis: {
          categories: labelText,
          labels: {
            rotate: -45,
            style: {
              fontSize: '9px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'จำนวนบุคลากร (คน)'
          },
          labels: {
            formatter: function (val) {
              return val + " คน";
            }
          }
        },
        grid: {
          borderColor: '#e0e0e0',
          strokeDashArray: 5
        }
      };
    },

    async refreshData() {
      await this.personnel_etcQueryAll();
    },

    getSubjectTypeColor(subjectType) {
      const colors = {
        'วิชาสามัญ': 'blue',
        'วิชาชีพ': 'green',
        'วิชาเฉพาะ': 'orange',
        'วิชาพื้นฐาน': 'primary'
      };
      return colors[subjectType] || 'grey';
    },

    getCountColor(count) {
      const num = parseInt(count) || 0;
      if (num === 0) return "grey";
      if (num <= 5) return "red";
      if (num <= 15) return "orange";
      if (num <= 30) return "blue";
      return "green";
    },

    getProgressValue(count) {
      const max = Math.max(...this.personnel_etcs.map(item => parseInt(item.countAll)));
      return (parseInt(count) / max) * 100;
    },

    exportToExcel() {
      this.showSnackbar("กำลังส่งออกไฟล์ Excel...", "info");
      // Implementation for Excel export
    },

    exportToPDF() {
      this.showSnackbar("กำลังส่งออกไฟล์ PDF...", "info");
      // Implementation for PDF export
    },

    exportChart() {
      this.showSnackbar("กำลังส่งออกกราฟ...", "info");
      // Implementation for chart export
    },

    showSnackbar(text, color = "success") {
      const iconMap = {
        success: "mdi-check-circle",
        error: "mdi-alert-circle",
        warning: "mdi-alert",
        info: "mdi-information"
      };

      this.snackbar = {
        show: true,
        text,
        color,
        icon: iconMap[color] || "mdi-information",
        timeout: 3000
      };
    }
  },

  computed: {
    filteredPersonnelData() {
      let data = [...this.personnel_etcs];
      
      if (this.subjectTypeFilter) {
        data = data.filter(item => item.subject_typeName === this.subjectTypeFilter);
      }
      
      return data;
    },

    subjectTypes() {
      const types = [...new Set(this.personnel_etcs.map(item => item.subject_typeName))];
      return types.map(type => ({ text: type, value: type }));
    },

    summaryStats() {
      const data = this.personnel_etcs;
      const totalPersonnel = data.reduce((sum, item) => sum + parseInt(item.countAll), 0);
      const totalBranches = data.length;
      const subjectTypeCount = new Set(data.map(item => item.subject_typeName)).size;
      const averagePerBranch = totalPersonnel / totalBranches;

      return [
        {
          title: "รวมบุคลากร",
          value: totalPersonnel.toLocaleString(),
          icon: "mdi-account-group",
          color: "primary"
        },
        {
          title: "จำนวนสาขา",
          value: totalBranches.toLocaleString(),
          icon: "mdi-school",
          color: "success"
        },
        {
          title: "ประเภทวิชา",
          value: subjectTypeCount.toLocaleString(),
          icon: "mdi-format-list-bulleted",
          color: "info"
        },
        {
          title: "เฉลี่ย/สาขา",
          value: averagePerBranch.toFixed(1),
          icon: "mdi-calculator",
          color: "warning"
        }
      ];
    },

    totalPersonnel() {
      return this.personnel_etcs.reduce((sum, item) => sum + parseInt(item.countAll), 0);
    },

    topBranch() {
      if (!this.personnel_etcs.length) return null;
      return this.personnel_etcs.reduce((max, item) => 
        parseInt(item.countAll) > parseInt(max.countAll) ? item : max
      );
    },

    averagePersonnel() {
      if (!this.personnel_etcs.length) return 0;
      const total = this.personnel_etcs.reduce((sum, item) => sum + parseInt(item.countAll), 0);
      return (total / this.personnel_etcs.length).toFixed(1);
    },

    medianPersonnel() {
      if (!this.personnel_etcs.length) return 0;
      const sorted = this.personnel_etcs.map(item => parseInt(item.countAll)).sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0 
        ? ((sorted[mid - 1] + sorted[mid]) / 2).toFixed(1)
        : sorted[mid];
    },

    standardDeviation() {
      if (!this.personnel_etcs.length) return 0;
      const values = this.personnel_etcs.map(item => parseInt(item.countAll));
      const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
      const variance = values.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / values.length;
      return Math.sqrt(variance).toFixed(2);
    },

    chartTitle() {
      const typeText = this.selectedChartType === "bar" ? "แนวนอน" : 
                      this.selectedChartType === "column" ? "แนวตั้ง" : "";
      const limitText = this.chartDataLimit > 0 ? ` (${this.chartDataLimit} อันดับแรก)` : "";
      return `อัตรากำลังแยกตามสาขาวิชา ${typeText}${limitText}`;
    }
  }
};
</script>

<style scoped>
.personnel-report-container {
  padding: 16px;
  background: linear-gradient(135deg, #f5f5f5 0%, #90a4ae 100%);
  min-height: 100vh;
}

.stat-card {
  border-radius: 15px !important;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.chart-container {
  border-radius: 15px !important;
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
  transition: all 0.3s ease;
}

.chart-container:hover {
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.1);
}

.stats-detail-card {
  border-radius: 15px !important;
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
}

.stats-detail-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.search-field {
  border-radius: 25px;
}

.enhanced-table {
  border-radius: 0 0 15px 15px;
  background: white;
}

.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 0 0 15px 15px;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.enhanced-table >>> .v-data-table-header th {
  background: linear-gradient(145deg, #455a64 0%, #455a64 100%) !important;
  color: white !important;
  font-weight: 600;
  border: none !important;
}

.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.v-btn-toggle {
  border-radius: 25px !important;
  overflow: hidden;
}

.v-btn-toggle >>> .v-btn {
  border-radius: 0 !important;
}

.v-progress-linear {
  border-radius: 10px !important;
}

/* Export menu styling */
.v-menu >>> .v-list {
  border-radius: 10px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.v-menu >>> .v-list-item:hover {
  background: linear-gradient(145deg, #f5f5f5 0%, #e8e8e8 100%);
}

/* Custom scrollbar for table */
.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Loading states */
.v-data-table >>> .v-data-table__progress {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Snackbar styling */
.v-snackbar >>> .v-snack__wrapper {
  border-radius: 10px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Button hover effects */
.v-btn {
  transition: all 0.3s ease;
}

.v-btn:hover:not(.v-btn--disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* Card title styling */
.v-card-title {
  background: linear-gradient(145deg, #455a64 0%, #455a64 100%);
  border-radius: 15px 15px 0 0;
}

/* Form field styling */
.v-text-field--outlined >>> fieldset,
.v-select--outlined >>> fieldset {
  border-color: #e0e0e0;
  border-radius: 15px;
}

.v-text-field--outlined:hover >>> fieldset,
.v-select--outlined:hover >>> fieldset {
  border-color: #455a64;
}

.v-text-field--outlined.v-input--is-focused >>> fieldset,
.v-select--outlined.v-input--is-focused >>> fieldset {
  border-color: #455a64;
  border-width: 2px;
}

/* Chart animations */
#chart {
  transition: all 0.3s ease;
}

/* Statistics detail cards */
.stats-detail-card >>> .v-card-title {
  background: transparent !important;
  color: inherit !important;
  padding-bottom: 8px;
}

/* Responsive design improvements */
@media (max-width: 1024px) {
  .enhanced-table >>> .v-data-table__wrapper {
    max-height: 500px;
  }
  
  .chart-container {
    margin-top: 16px;
  }
  
  .stats-detail-card {
    margin-bottom: 16px;
  }
}

@media (max-width: 768px) {
  .personnel-report-container {
    padding: 8px;
  }
  
  .stat-card {
    margin-bottom: 8px;
  }
  
  .enhanced-table >>> .v-data-table__wrapper {
    max-height: 400px;
  }
  
  .v-card-title {
    font-size: 1.1rem !important;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .v-card-title .text-h5 {
    font-size: 1.1rem !important;
    margin-bottom: 8px;
  }
  
  .v-btn-toggle {
    margin-bottom: 8px;
    width: 100%;
  }
  
  /* Hide some columns on mobile */
  .enhanced-table >>> th:nth-child(2),
  .enhanced-table >>> td:nth-child(2) {
    display: none;
  }
  
  /* Adjust chart height on mobile */
  .chart-container >>> #chart {
    height: 350px !important;
  }
}

@media (max-width: 480px) {
  .v-btn-toggle {
    flex-direction: row;
    justify-content: center;
  }
  
  .v-btn-toggle >>> .v-btn {
    flex: 1;
    margin: 0 2px;
  }
  
  .stat-card >>> .v-card-text {
    padding: 12px;
  }
  
  .stat-card >>> .text-h5 {
    font-size: 1.3rem !important;
  }
  
  .stats-detail-card >>> .v-card-text {
    padding: 12px;
  }
  
  /* Stack filter controls vertically on small screens */
  .v-card-subtitle >>> .v-row {
    flex-direction: column;
  }
  
  .v-card-subtitle >>> .v-col {
    margin-bottom: 8px;
  }
}

/* Print styles */
@media print {
  .personnel-report-container {
    background: white !important;
    padding: 0;
  }
  
  .v-btn,
  .search-field,
  .v-btn-toggle,
  .v-menu {
    display: none !important;
  }
  
  .v-card {
    box-shadow: none !important;
    border: 1px solid #ddd;
  }
  
  .enhanced-table >>> .v-data-table__wrapper {
    max-height: none !important;
  }
  
  .chart-container {
    page-break-inside: avoid;
  }
}

/* Dark theme support */
.theme--dark .chart-container {
  background: linear-gradient(145deg, #2d2d2d 0%, #1e1e1e 100%);
}

.theme--dark .enhanced-table {
  background: #2d2d2d;
}

.theme--dark .stat-card {
  background: linear-gradient(145deg, #2d2d2d 0%, #1e1e1e 100%);
}

.theme--dark .stats-detail-card {
  background: linear-gradient(145deg, #2d2d2d 0%, #1e1e1e 100%);
}

/* Accessibility improvements */
.v-btn:focus,
.v-text-field:focus,
.v-select:focus {
  outline: 2px solid #455a64;
  outline-offset: 2px;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .v-chip {
    border: 2px solid currentColor;
  }
  
  .enhanced-table >>> tbody tr:hover {
    background-color: #000 !important;
    color: #fff !important;
  }
  
  .v-progress-linear {
    border: 1px solid currentColor;
  }
}

/* Reduced motion for accessibility */
@media (prefers-reduced-motion: reduce) {
  .stat-card:hover,
  .v-btn:hover,
  .v-chip:hover,
  .chart-container:hover,
  .stats-detail-card:hover {
    transform: none;
  }
  
  .v-data-table >>> .v-data-table__progress {
    animation: none;
  }
  
  #chart {
    transition: none;
  }
}

/* Focus indicators for better accessibility */
.v-chip:focus {
  outline: 2px solid #455a64;
  outline-offset: 2px;
}

.v-progress-linear:focus {
  outline: 2px solid #455a64;
  outline-offset: 2px;
}

/* Custom scrollbar styling for chart container */
.chart-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.chart-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.chart-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.chart-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>